import {OtsDateTime} from "../components/OtsDateTime";
import {billingDecisionInputs, billingDecisionChecks, billingOutputLabel} from "./billing-decision-display";
import {DescriptionItem, DescriptionList} from "../components/DescriptionList";
import {OtsTimeline} from "../components/OtsTimeline";
import {BillingDecisionFlow} from "./BillingDecisionFlow";
import {BillingScheduleTimeline} from "./BillingScheduleTimeline";
import {BillingAfeSources} from "./BillingAfeSources";
import {RichMessageText} from "../diagnostics/RichMessageText";
import {DecisionMessage} from "../decision-graph/DecisionMessage";
import type {BillingDecisionTrace} from "./ticket-cost-types";

/** The default view explains the outcome; the full evaluation stays available on demand. */
export const BillingDecisionDetails = ({trace}: {trace: BillingDecisionTrace}) => {
  const finalOutput = trace.steps.find(step => step.code === "FINAL_BILLING_LOCATION");
  const outputs = trace.steps.filter(step => step.code === "BILLING_LOCATION");
  const reason = trace.steps.find(step => step.code === "MATCHED_STAGE" && step.entityId === outputs[0]?.entityId)
    ?? trace.steps.find(step => step.code === "CODED_LOCATION" && step.entityId === outputs[0]?.entityId)
    ?? trace.steps.find(step => step.code === "PRODUCT_SITE_AFE" || step.code === "CONTAINER_AFE" || step.code === "SITE_FALLBACK");
  return <section aria-label="Why this billing location?" className="mt-4 space-y-4 text-[var(--ots-text)]">
    <div>
      <h3 className="text-sm font-semibold">Why this billing location?</h3>
      <p className="mt-1 text-base font-semibold">{finalOutput ? finalOutput.explanation : outputs.length === 1 ? outputs[0].message ? <RichMessageText message={outputs[0].message} /> : billingOutputLabel(trace, outputs[0])
        : outputs.length > 1 ? `${outputs.length} billing mappings selected.`
        : trace.steps.length ? "No eligible billing location" : "No billing decision available"}</p>
      {outputs.length === 1 && reason && <div className="mt-1 text-sm text-[var(--ots-text-muted)]">{reason.message ? <DecisionMessage message={reason.message} /> : reason.explanation}</div>}
    </div>
    <BillingDecisionFlow trace={trace} />
    {!!trace.intervals.length && <details className="rounded-lg border border-[var(--ots-outline-variant)] p-3">
      <summary className="cursor-pointer text-sm font-semibold">Schedule — why at this time?</summary>
      <div className="mt-3"><BillingScheduleTimeline trace={trace} /></div>
    </details>}
    {!!outputs.length && <details className="rounded-lg border border-[var(--ots-outline-variant)] p-3">
      <summary className="cursor-pointer text-sm font-semibold">Expenditure code sources — where did this code come from?</summary>
      <div className="mt-3"><BillingAfeSources trace={trace} /></div>
    </details>}
    <details className="rounded-lg border border-[var(--ots-outline-variant)] p-3">
      <summary className="cursor-pointer text-sm font-semibold">How this was decided</summary>
      <div className="mt-3 space-y-4">
        <p className="text-xs">Evaluated {trace.effectiveDttm == null ? "without a ticket timestamp" : <>at <OtsDateTime value={trace.effectiveDttm} /></>}.</p>
        <DescriptionList className="gap-2">
          {billingDecisionInputs(trace).map(({label, value}) => <DescriptionItem key={label} label={label}>{value}</DescriptionItem>)}
        </DescriptionList>
        <OtsTimeline ariaLabel="Billing decision path" density="compact" emptyState="No decision steps were provided."
          items={billingDecisionChecks(trace).map((check, index) => ({
            id: `${index}-${check.code}`,
            tone: check.outcome === "selected" ? "success" : check.outcome === "rejected" ? "danger" : "neutral",
            label: <>{check.title}{check.entityName && <span className="ml-1 font-normal">· {check.entityName}</span>}</>,
            description: check.message ? <DecisionMessage message={check.message} expanded /> : check.description,
          }))} />
      </div>
    </details>
  </section>;
};
